import type { FC, ReactNode } from 'react'
import type { Hole } from '@/types'

import { Fragment } from 'react'
import { cn } from '@/lib/utils'
import { formatNumber, pad } from '@/lib/format'
import { SectionLabel } from '@/components/generals/section-label'
import { SplitReveal } from '@/components/generals/split-reveal'
import { EnquiryButton } from '@/components/generals/enquiry-button'

import { backNine, frontNine, holes } from '@/constants/course'
import { teeSets } from '@/constants/pages'

const yardsFor = (hole: Hole, factor: number): number => Math.round(hole.yards * factor)

const totalFor = (list: Hole[], factor: number): number => list.reduce((sum, hole) => sum + yardsFor(hole, factor), 0)

const parFor = (list: Hole[]): number => list.reduce((sum, hole) => sum + hole.par, 0)

interface ScorecardSectionProps {
    index: string
}

/** The full scorecard with yardages from all four sets of tees. */
export const ScorecardSection: FC<ScorecardSectionProps> = ({ index }): ReactNode => {
    const halves = [
        { label: 'Out', list: frontNine },
        { label: 'In', list: backNine }
    ]

    return (
        <section id='scorecard' aria-labelledby='scorecard-heading' className='relative z-10 bg-paper text-charcoal'>
            <div className='px-5 py-24 sm:px-8 lg:px-12 lg:py-32'>
                <div className='grid gap-10 lg:grid-cols-12 lg:gap-8'>
                    <div className='lg:col-span-7'>
                        <SectionLabel index={index} className='text-charcoal/70'>
                            The scorecard
                        </SectionLabel>

                        <SplitReveal
                            as='h2'
                            id='scorecard-heading'
                            className='mt-6 text-[clamp(2.1rem,4.2vw,4.2rem)] font-light leading-[1.02] tracking-[-0.03em]'
                        >
                            Four sets of tees, <em className='font-serif font-normal'>one fair test.</em>
                        </SplitReveal>
                    </div>

                    <div data-reveal className='self-end lg:col-span-4 lg:col-start-9'>
                        <p className='text-base leading-relaxed text-charcoal/70'>
                            From 5,131 yards on the Short tees to 7,048 from the Championship tees. Choose the set that lets you reach most greens in regulation, and the course will reward you for it.
                        </p>

                        <EnquiryButton
                            prefill={{ type: 'tee-time', course: 'Championship 18' }}
                            label='Request a tee time'
                            className='mt-8'
                        />
                    </div>
                </div>

                <div data-reveal className='-mx-5 mt-14 overflow-x-auto px-5 sm:mx-0 sm:px-0 lg:mt-20'>
                    <table className='w-full min-w-[760px] border-collapse text-left text-sm'>
                        <caption className='sr-only'>
                            Scorecard for the Championship 18, with par, stroke index and yardage from each set of tees
                        </caption>

                        <thead>
                            <tr className='border-b border-charcoal/20 font-mono text-[10px] uppercase tracking-[0.18em] text-charcoal/60'>
                                <th scope='col' className='py-4 pr-4 font-normal'>
                                    Hole
                                </th>

                                <th scope='col' className='py-4 pr-4 font-normal'>
                                    Name
                                </th>

                                {teeSets.map((tee) => (
                                    <th key={tee.name} scope='col' className='py-4 pr-4 text-right font-normal'>
                                        <span className='inline-flex items-center gap-2'>
                                            <span aria-hidden='true' className='size-2.5 rounded-full border border-charcoal/30' style={{ backgroundColor: tee.colour }} />
                                            {tee.name}
                                        </span>
                                    </th>
                                ))}

                                <th scope='col' className='py-4 pr-4 text-right font-normal'>
                                    Par
                                </th>

                                <th scope='col' className='py-4 text-right font-normal'>
                                    Index
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {halves.map((half) => (
                                <Fragment key={half.label}>
                                    {half.list.map((hole) => (
                                        <tr key={hole.number} className='border-b border-charcoal/10 transition-colors hover:bg-sand/60'>
                                            <th scope='row' className='py-3 pr-4 font-mono text-[12px] font-normal tabular-nums text-charcoal/60'>
                                                {pad(hole.number)}
                                            </th>

                                            <td className='py-3 pr-4 font-medium'>
                                                {hole.name}
                                            </td>

                                            {teeSets.map((tee) => (
                                                <td key={tee.name} className='py-3 pr-4 text-right tabular-nums'>
                                                    {yardsFor(hole, tee.factor)}
                                                </td>
                                            ))}

                                            <td className='py-3 pr-4 text-right tabular-nums'>
                                                {hole.par}
                                            </td>

                                            <td className='py-3 text-right tabular-nums text-charcoal/60'>
                                                {hole.strokeIndex}
                                            </td>
                                        </tr>
                                    ))}

                                    <tr className='border-b border-charcoal/20 bg-sand font-medium'>
                                        <th scope='row' colSpan={2} className='py-3.5 pl-3 pr-4 font-mono text-[11px] uppercase tracking-[0.2em]'>
                                            {half.label}
                                        </th>

                                        {teeSets.map((tee) => (
                                            <td key={tee.name} className='py-3.5 pr-4 text-right tabular-nums'>
                                                {formatNumber(totalFor(half.list, tee.factor))}
                                            </td>
                                        ))}

                                        <td className='py-3.5 pr-4 text-right tabular-nums'>
                                            {parFor(half.list)}
                                        </td>

                                        <td className='py-3.5' />
                                    </tr>
                                </Fragment>
                            ))}

                            <tr className={cn('bg-forest font-medium text-bone')}>
                                <th scope='row' colSpan={2} className='py-4 pl-3 pr-4 font-mono text-[11px] uppercase tracking-[0.2em]'>
                                    Total
                                </th>

                                {teeSets.map((tee) => (
                                    <td key={tee.name} className='type-wide py-4 pr-4 text-right tabular-nums'>
                                        {formatNumber(totalFor(holes, tee.factor))}
                                    </td>
                                ))}

                                <td className='type-wide py-4 pr-4 text-right tabular-nums'>
                                    {parFor(holes)}
                                </td>

                                <td className='py-4' />
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    )
}
